import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { monitorAPI } from '../services/api';

type UsageExportAccount = 'main' | 'datapool' | 'impactplan';
type TableExportReport = 'service-lines' | 'kits' | 'addresses' | 'routers' | 'service-line-kits';
type UsageExportColumn = 'serviceLineNumber' | 'nickname' | 'productReferenceId' | 'kitSerialNumber' | 'active' | 'startDate' | 'endDate' | 'packageLimit';

const exportAccounts: Array<{ account: UsageExportAccount; label: string }> = [
  { account: 'main', label: 'Main' },
  { account: 'datapool', label: 'DataPool' },
  { account: 'impactplan', label: 'Impact Plan' },
];

const usageExportColumns: Array<{ key: UsageExportColumn; label: string; helper?: string }> = [
  { key: 'serviceLineNumber', label: 'Service Line Number' },
  { key: 'nickname', label: 'Nickname' },
  { key: 'productReferenceId', label: 'Product Reference ID' },
  { key: 'kitSerialNumber', label: 'Kit Serial Number' },
  { key: 'active', label: 'Active' },
  { key: 'startDate', label: 'Start Date' },
  { key: 'endDate', label: 'End Date' },
  { key: 'packageLimit', label: 'Package Limit', helper: 'DataPool only' },
];

const defaultUsageExportColumns: UsageExportColumn[] = usageExportColumns.map((column) => column.key);

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const tableExportRows: Array<{
  report: TableExportReport;
  title: string;
  description: string;
  icon: string;
}> = [
  {
    report: 'service-lines',
    title: 'Service lines',
    description: 'Exports service line records for the selected account.',
    icon: 'fas fa-list',
  },
  {
    report: 'kits',
    title: 'Kits',
    description: 'Exports kit records for the selected account.',
    icon: 'fas fa-satellite-dish',
  },
  {
    report: 'addresses',
    title: 'Addresses',
    description: 'Exports address records for the selected account.',
    icon: 'fas fa-location-dot',
  },
  {
    report: 'routers',
    title: 'Routers',
    description: 'Exports router records linked to kits for the selected account.',
    icon: 'fas fa-wifi',
  },
  {
    report: 'service-line-kits',
    title: 'Service line + kit number',
    description: 'Exports nickname, service line, kit serial, product, active, start date, and end date. DataPool also includes package limit.',
    icon: 'fas fa-link',
  },
];

const DataUsageExports: React.FC = () => {
  const navigate = useNavigate();
  const [exportingKey, setExportingKey] = useState<string | null>(null);
  const [usageExportModalOpen, setUsageExportModalOpen] = useState(false);
  const [selectedUsageAccounts, setSelectedUsageAccounts] = useState<UsageExportAccount[]>(['main', 'datapool', 'impactplan']);
  const [usageExportStartMonth, setUsageExportStartMonth] = useState('2024-01');
  const [usageExportEndMonth, setUsageExportEndMonth] = useState(currentMonthKey());
  const [selectedUsageColumns, setSelectedUsageColumns] = useState<UsageExportColumn[]>(defaultUsageExportColumns);

  const [dataPoolMonthlyModalOpen, setDataPoolMonthlyModalOpen] = useState(false);
  const [dataPoolMonthlyStartMonth, setDataPoolMonthlyStartMonth] = useState('2024-01');
  const [dataPoolMonthlyEndMonth, setDataPoolMonthlyEndMonth] = useState(currentMonthKey());
  const [selectedDataPoolMonthlyColumns, setSelectedDataPoolMonthlyColumns] = useState<UsageExportColumn[]>(defaultUsageExportColumns);

  const openUsageExportModal = (account?: UsageExportAccount) => {
    setSelectedUsageAccounts(account ? [account] : ['main', 'datapool', 'impactplan']);
    setSelectedUsageColumns(defaultUsageExportColumns);
    setUsageExportModalOpen(true);
  };

  const toggleUsageAccount = (account: UsageExportAccount) => {
    setSelectedUsageAccounts((current) => (
      current.includes(account)
        ? current.filter((item) => item !== account)
        : [...current, account]
    ));
  };

  const toggleUsageColumn = (column: UsageExportColumn) => {
    setSelectedUsageColumns((current) => (
      current.includes(column)
        ? current.filter((item) => item !== column)
        : [...current, column]
    ));
  };

  const toggleDataPoolMonthlyColumn = (column: UsageExportColumn) => {
    setSelectedDataPoolMonthlyColumns((current) => (
      current.includes(column)
        ? current.filter((item) => item !== column)
        : [...current, column]
    ));
  };

  const saveBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadDataUsageExport = async () => {
    if (selectedUsageAccounts.length === 0) {
      toast.error('Select at least one account');
      return;
    }

    if (selectedUsageColumns.length === 0) {
      toast.error('Select at least one column');
      return;
    }

    if (!usageExportStartMonth || !usageExportEndMonth || usageExportStartMonth > usageExportEndMonth) {
      toast.error('Choose a valid month range');
      return;
    }

    setExportingKey('usage:custom');
    try {
      const blob = await monitorAPI.exportCustomDataUsageReport({
        accounts: selectedUsageAccounts,
        startMonth: usageExportStartMonth,
        endMonth: usageExportEndMonth,
        columns: selectedUsageColumns,
      });
      const dateStamp = new Date().toISOString().slice(0, 10);
      saveBlob(blob, `${selectedUsageAccounts.length === 3 ? 'combined' : selectedUsageAccounts.join('_')}_data_usage_report_${dateStamp}.xlsx`);
      toast.success('Export downloaded');
      setUsageExportModalOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to download export');
    } finally {
      setExportingKey(null);
    }
  };

  const downloadDataPoolMonthlyExport = async () => {
    if (selectedDataPoolMonthlyColumns.length === 0) {
      toast.error('Select at least one column');
      return;
    }

    if (!dataPoolMonthlyStartMonth || !dataPoolMonthlyEndMonth || dataPoolMonthlyStartMonth > dataPoolMonthlyEndMonth) {
      toast.error('Choose a valid month range');
      return;
    }

    setExportingKey('usage:datapool-monthly');
    try {
      const blob = await monitorAPI.exportDataPoolMonthlyReport({
        startMonth: dataPoolMonthlyStartMonth,
        endMonth: dataPoolMonthlyEndMonth,
        columns: selectedDataPoolMonthlyColumns,
      });
      const dateStamp = new Date().toISOString().slice(0, 10);
      saveBlob(blob, `datapool_monthly_${dateStamp}.xlsx`);
      toast.success('Export downloaded');
      setDataPoolMonthlyModalOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to download export');
    } finally {
      setExportingKey(null);
    }
  };

  const downloadTableExport = async (account: UsageExportAccount, report: TableExportReport) => {
    const exportKey = `${report}:${account}`;
    setExportingKey(exportKey);
    try {
      const blob = await monitorAPI.exportDataTableReport(account, report);
      const dateStamp = new Date().toISOString().slice(0, 10);
      saveBlob(blob, `${account}_${report.replace(/-/g, '_')}_${dateStamp}.xlsx`);
      toast.success('Export downloaded');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to download export');
    } finally {
      setExportingKey(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-950 via-sky-950 to-emerald-950 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_18%_25%,rgba(56,189,248,0.25),transparent_45%),radial-gradient(circle_at_82%_20%,rgba(16,185,129,0.2),transparent_50%)]" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,0.14)]" />
              Export center
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Data Usage Exports</h1>
            <p className="max-w-2xl text-white/70">Build Excel reports from usage, service line, kit, address, and router data.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/data-usage')}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
            >
              <i className="fas fa-arrow-left"></i>
              Back to Data Usage
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-black text-gray-900">Usage report exports</h2>
            <p className="mt-1 text-sm text-gray-500">Create one Excel workbook for selected accounts, columns, and month range.</p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-emerald-50 p-5">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                <i className="fas fa-file-excel"></i>
              </div>
              <div>
                <h3 className="font-black text-slate-900">Configure usage export</h3>
                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                  Select Main, DataPool, Impact Plan, or all three; choose the month range and only the columns you need.
                </p>
              </div>
            </div>
            <button
              onClick={() => openUsageExportModal()}
              disabled={!!exportingKey}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-bold text-white transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              <i className="fas fa-sliders"></i>
              Configure Export
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-black text-gray-900">Operational data exports</h2>
            <p className="mt-1 text-sm text-gray-500">Export service line, kit, address, router, and joined reports you normally review manually.</p>
          </div>
          <div className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            {tableExportRows.length * exportAccounts.length} exports available
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-100">
          <table className="min-w-[980px] w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Export</th>
                <th className="px-4 py-3">What it contains</th>
                {exportAccounts.map((account) => (
                  <th key={account.account} className="px-4 py-3 text-center">{account.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {tableExportRows.map((row) => (
                <tr key={row.report} className="align-top">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                        <i className={row.icon}></i>
                      </span>
                      <span className="font-black text-slate-900">{row.title}</span>
                    </div>
                  </td>
                  <td className="max-w-md px-4 py-4 text-slate-600">{row.description}</td>
                  {exportAccounts.map((account) => {
                    const exportKey = `${row.report}:${account.account}`;
                    const isExporting = exportingKey === exportKey;
                    return (
                      <td key={account.account} className="px-4 py-4 text-center">
                        <button
                          onClick={() => downloadTableExport(account.account, row.report)}
                          disabled={!!exportingKey}
                          className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-bold text-slate-800 ring-1 ring-slate-200 transition-all hover:bg-slate-50 hover:ring-slate-300 disabled:cursor-not-allowed disabled:text-slate-400"
                        >
                          <i className={`fas ${isExporting ? 'fa-spinner animate-spin' : 'fa-file-export'}`}></i>
                          {isExporting ? 'Exporting' : 'Export'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-black text-gray-900">DataPool monthly</h2>
            <p className="mt-1 text-sm text-gray-500">Export DataPool usage split into Capped (≤ 450 GB) and Uncapped (&gt; 450 GB) sheets.</p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 via-white to-emerald-50 p-5">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-purple-100 text-purple-700">
                <i className="fas fa-layer-group"></i>
              </div>
              <div>
                <h3 className="font-black text-slate-900">DataPool monthly split</h3>
                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                  Choose the month range and columns. The export produces two sheets: Capped and Uncapped.
                </p>
              </div>
            </div>
            <button
              onClick={() => setDataPoolMonthlyModalOpen(true)}
              disabled={!!exportingKey}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-purple-600 px-5 py-3 text-sm font-bold text-white transition-all hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              <i className="fas fa-file-export"></i>
              Export DataPool
            </button>
          </div>
        </div>
      </div>

      {usageExportModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-6xl max-h-[92vh] overflow-hidden">
            <div className="border-b p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-black text-slate-900">Configure data usage export</h3>
                  <p className="mt-1 text-sm text-slate-500">Choose the workbook sheets, month range, and columns before exporting.</p>
                </div>
                <button className="text-gray-500 hover:text-gray-700" onClick={() => setUsageExportModalOpen(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            <div className="max-h-[calc(92vh-170px)] overflow-y-auto bg-[#F8F9FB] px-8 py-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <section className="rounded-xl border border-gray-100/80 bg-gray-50/70 p-5">
                    <h4 className="text-base font-bold text-gray-900"><span className="mr-1.5">1.</span>Accounts / sheets</h4>
                    <p className="mb-4 mt-1 text-[13px] leading-relaxed text-gray-500">Each selected account becomes its own Excel sheet.</p>
                    <div className="flex flex-wrap gap-3">
                      {exportAccounts.map((account) => {
                        const isSelected = selectedUsageAccounts.includes(account.account);
                        return (
                          <button
                            key={account.account}
                            type="button"
                            onClick={() => toggleUsageAccount(account.account)}
                            className={`relative flex cursor-pointer items-center gap-3 rounded-xl border-2 px-5 py-4 transition-all duration-200 ${
                              isSelected
                                ? 'border-blue-500/30 bg-white shadow-md'
                                : 'border-gray-200/80 bg-gray-50 hover:border-gray-300 hover:bg-gray-100/50 hover:shadow-sm'
                            }`}
                          >
                            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors duration-200 ${
                              isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300 bg-white'
                            }`}>
                              {isSelected && <i className="fas fa-check text-[10px] text-white"></i>}
                            </span>
                            <span className={`text-[15px] ${isSelected ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{account.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className="rounded-xl border border-gray-100/80 bg-gray-50/70 p-5">
                    <h4 className="text-base font-bold text-gray-900"><span className="mr-1.5">2.</span>Month range</h4>
                    <p className="mb-4 mt-1 text-[13px] leading-relaxed text-gray-500">Monthly columns will be generated from this range.</p>
                    <div className="grid grid-cols-2 gap-4">
                      <label>
                        <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">From</span>
                        <input
                          type="month"
                          value={usageExportStartMonth}
                          onChange={(event) => setUsageExportStartMonth(event.target.value)}
                          className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-[15px] font-medium text-gray-900 outline-none transition-colors hover:border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                        />
                      </label>
                      <label>
                        <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">To</span>
                        <input
                          type="month"
                          value={usageExportEndMonth}
                          onChange={(event) => setUsageExportEndMonth(event.target.value)}
                          className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-[15px] font-medium text-gray-900 outline-none transition-colors hover:border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                        />
                      </label>
                    </div>
                  </section>
                </div>

                <section className="rounded-xl border border-gray-100/80 bg-gray-50/70 p-5">
                  <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <h4 className="text-base font-bold text-gray-900"><span className="mr-1.5">3.</span>Columns</h4>
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                          {selectedUsageColumns.length} of {usageExportColumns.length}
                        </span>
                      </div>
                      <p className="text-[13px] leading-relaxed text-gray-500">Choose which detail columns appear before the monthly usage data.</p>
                    </div>

                    <div className="flex shrink-0 self-start rounded-lg bg-gray-200/60 p-0.5">
                      <button
                        type="button"
                        onClick={() => setSelectedUsageColumns(defaultUsageExportColumns)}
                        className={`cursor-pointer rounded-md px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 ${
                          selectedUsageColumns.length === usageExportColumns.length
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedUsageColumns(['serviceLineNumber'])}
                        className={`cursor-pointer rounded-md px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 ${
                          selectedUsageColumns.length === 1 && selectedUsageColumns.includes('serviceLineNumber')
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Service line
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {usageExportColumns.map((column) => {
                      const isSelected = selectedUsageColumns.includes(column.key);
                      return (
                        <button
                          key={column.key}
                          type="button"
                          onClick={() => toggleUsageColumn(column.key)}
                          className={`relative flex w-full cursor-pointer items-start gap-3 rounded-lg border-2 p-4 text-left transition-colors duration-200 ${
                            isSelected
                              ? 'border-blue-500/30 bg-white shadow-sm'
                              : 'border-gray-200/80 bg-gray-50 hover:border-gray-300 hover:bg-gray-100/50'
                          }`}
                        >
                          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors duration-200 ${
                            isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300 bg-white'
                          }`}>
                            {isSelected && <i className="fas fa-check text-[10px] text-white"></i>}
                          </span>
                          <span className="flex min-w-0 flex-col">
                            <span className={`text-[15px] leading-snug ${isSelected ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{column.label}</span>
                            {column.helper && <span className="mt-0.5 text-xs text-gray-400">{column.helper}</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>

                <section className="rounded-xl border border-blue-100/60 bg-blue-50/50 p-5">
                  <h4 className="mb-4 flex items-center gap-2 text-sm font-bold text-gray-900">
                    <i className="fas fa-file-excel text-blue-600"></i>
                    Preview
                  </h4>
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div>
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-table-columns text-[13px] text-blue-600"></i>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Sheets</span>
                      </div>
                      <p className="text-[13px] font-medium text-gray-700">
                        {selectedUsageAccounts.length > 0 ? selectedUsageAccounts.map((account) => exportAccounts.find((item) => item.account === account)?.label).join(', ') : 'None selected'}
                      </p>
                    </div>
                    <div>
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-calendar text-[13px] text-blue-600"></i>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Months</span>
                      </div>
                      <p className="text-[13px] font-medium text-gray-700">{usageExportStartMonth || 'Not set'} to {usageExportEndMonth || 'Not set'}</p>
                    </div>
                    <div>
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-columns text-[13px] text-blue-600"></i>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Columns</span>
                      </div>
                      <p className="text-[13px] font-medium text-gray-700">{selectedUsageColumns.length} selected + monthly usage columns</p>
                    </div>
                  </div>
                </section>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-100 bg-white px-8 py-5">
              <button
                onClick={() => setUsageExportModalOpen(false)}
                className="rounded-2xl bg-slate-100 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={downloadDataUsageExport}
                disabled={!!exportingKey}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                <i className={`fas ${exportingKey === 'usage:custom' ? 'fa-spinner animate-spin' : 'fa-file-export'}`}></i>
                {exportingKey === 'usage:custom' ? 'Exporting...' : 'Export Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {dataPoolMonthlyModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-6xl max-h-[92vh] overflow-hidden">
            <div className="border-b p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-black text-slate-900">Configure DataPool monthly export</h3>
                  <p className="mt-1 text-sm text-slate-500">Choose the month range and columns. Output is split into Capped and Uncapped sheets.</p>
                </div>
                <button className="text-gray-500 hover:text-gray-700" onClick={() => setDataPoolMonthlyModalOpen(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            <div className="max-h-[calc(92vh-170px)] overflow-y-auto bg-[#F8F9FB] px-8 py-6">
              <div className="space-y-6">
                <section className="rounded-xl border border-gray-100/80 bg-gray-50/70 p-5">
                  <h4 className="text-base font-bold text-gray-900"><span className="mr-1.5">1.</span>Month range</h4>
                  <p className="mb-4 mt-1 text-[13px] leading-relaxed text-gray-500">Monthly columns will be generated from this range.</p>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <label>
                      <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">From</span>
                      <input
                        type="month"
                        value={dataPoolMonthlyStartMonth}
                        onChange={(event) => setDataPoolMonthlyStartMonth(event.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-[15px] font-medium text-gray-900 outline-none transition-colors hover:border-gray-300 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/20"
                      />
                    </label>
                    <label>
                      <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">To</span>
                      <input
                        type="month"
                        value={dataPoolMonthlyEndMonth}
                        onChange={(event) => setDataPoolMonthlyEndMonth(event.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-[15px] font-medium text-gray-900 outline-none transition-colors hover:border-gray-300 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/20"
                      />
                    </label>
                  </div>
                </section>

                <section className="rounded-xl border border-gray-100/80 bg-gray-50/70 p-5">
                  <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <h4 className="text-base font-bold text-gray-900"><span className="mr-1.5">2.</span>Columns</h4>
                        <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-semibold text-purple-700">
                          {selectedDataPoolMonthlyColumns.length} of {usageExportColumns.length}
                        </span>
                      </div>
                      <p className="text-[13px] leading-relaxed text-gray-500">Choose which detail columns appear before the monthly usage data.</p>
                    </div>

                    <div className="flex shrink-0 self-start rounded-lg bg-gray-200/60 p-0.5">
                      <button
                        type="button"
                        onClick={() => setSelectedDataPoolMonthlyColumns(defaultUsageExportColumns)}
                        className={`cursor-pointer rounded-md px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 ${
                          selectedDataPoolMonthlyColumns.length === usageExportColumns.length
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedDataPoolMonthlyColumns(['serviceLineNumber'])}
                        className={`cursor-pointer rounded-md px-3.5 py-1.5 text-xs font-semibold transition-all duration-200 ${
                          selectedDataPoolMonthlyColumns.length === 1 && selectedDataPoolMonthlyColumns.includes('serviceLineNumber')
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Service line
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {usageExportColumns.map((column) => {
                      const isSelected = selectedDataPoolMonthlyColumns.includes(column.key);
                      return (
                        <button
                          key={column.key}
                          type="button"
                          onClick={() => toggleDataPoolMonthlyColumn(column.key)}
                          className={`relative flex w-full cursor-pointer items-start gap-3 rounded-lg border-2 p-4 text-left transition-colors duration-200 ${
                            isSelected
                              ? 'border-purple-500/30 bg-white shadow-sm'
                              : 'border-gray-200/80 bg-gray-50 hover:border-gray-300 hover:bg-gray-100/50'
                          }`}
                        >
                          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors duration-200 ${
                            isSelected ? 'border-purple-600 bg-purple-600' : 'border-gray-300 bg-white'
                          }`}>
                            {isSelected && <i className="fas fa-check text-[10px] text-white"></i>}
                          </span>
                          <span className="flex min-w-0 flex-col">
                            <span className={`text-[15px] leading-snug ${isSelected ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{column.label}</span>
                            {column.helper && <span className="mt-0.5 text-xs text-gray-400">{column.helper}</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>

                <section className="rounded-xl border border-purple-100/60 bg-purple-50/50 p-5">
                  <h4 className="mb-4 flex items-center gap-2 text-sm font-bold text-gray-900">
                    <i className="fas fa-file-excel text-purple-600"></i>
                    Preview
                  </h4>
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div>
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-table-columns text-[13px] text-purple-600"></i>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-purple-600">Sheets</span>
                      </div>
                      <p className="text-[13px] font-medium text-gray-700">Capped, Uncapped</p>
                    </div>
                    <div>
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-calendar text-[13px] text-purple-600"></i>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-purple-600">Months</span>
                      </div>
                      <p className="text-[13px] font-medium text-gray-700">{dataPoolMonthlyStartMonth || 'Not set'} to {dataPoolMonthlyEndMonth || 'Not set'}</p>
                    </div>
                    <div>
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-columns text-[13px] text-purple-600"></i>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-purple-600">Columns</span>
                      </div>
                      <p className="text-[13px] font-medium text-gray-700">{selectedDataPoolMonthlyColumns.length} selected + monthly usage columns</p>
                    </div>
                  </div>
                </section>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-100 bg-white px-8 py-5">
              <button
                onClick={() => setDataPoolMonthlyModalOpen(false)}
                className="rounded-2xl bg-slate-100 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={downloadDataPoolMonthlyExport}
                disabled={!!exportingKey}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-purple-600 px-5 py-3 text-sm font-bold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                <i className={`fas ${exportingKey === 'usage:datapool-monthly' ? 'fa-spinner animate-spin' : 'fa-file-export'}`}></i>
                {exportingKey === 'usage:datapool-monthly' ? 'Exporting...' : 'Export Excel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataUsageExports;
